import { BuildingOfficeIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import {
    CommunityOrgTypeSentinel,
    EVERYONE_SENTINEL,
    ReferralTypesOptions,
} from "@sp-crm/core";
import { countFiltersApplied } from "components/advanced-search/advanced-search-utility";
import { SearchGeoParamsCallout } from "components/community-search/search-bar/search-geo-params-callout";
import { SearchbarItemBadge } from "components/community-search/search-bar/searchbar-item-badge";
import { useDynamicCalloutPosition } from "components/community-search/search-bar/use-dynamic-callout-position";
import { useDebouncedState } from "components/shared/hooks";
import { SelectableUser, UserSelect } from "components/shared/user-select";
import { Callout } from "components/ui/callout";
import { Checkbox } from "components/ui/checkbox";
import { defaultLinkStyle } from "components/ui/link";
import { SearchInput } from "components/ui/search-input";
import { SecondaryButton } from "components/ui/secondary-button";
import {
    AdvancedSearchCondition,
    LayoutSectionParentKey,
    SearchGeoParams,
} from "generated/graphql";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useCanSeeReferralSourceOwners } from "store/selectors/hooks";
import { ReferenceFilter } from "store/slices/reference-dashboard";
import { EntityMoreFilter, EntityMoreFilterValue } from "./entity-more-filter";

interface ReferenceSearchBarProps {
    value: ReferenceFilter;
    onChange: (filter: ReferenceFilter) => void;
    onResetMapView?: () => void;
}

export const ReferenceSearchBar: React.FC<ReferenceSearchBarProps> = props => {
    const assignedToVisible = useCanSeeReferralSourceOwners();
    const { value, onChange, onResetMapView } = props;

    const [geoParamsRef, geoParamsPosition] = useDynamicCalloutPosition();

    const [searchTerm, setSearchTerm, searchTermDebounced] = useDebouncedState(
        value.search,
        400,
    );

    useEffect(() => {
        if (searchTermDebounced !== value.search) {
            onChange({
                ...value,
                search: searchTermDebounced,
            });
        }
    }, [searchTermDebounced]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOrgTypeChanged = (orgTypes: string[]) => {
        onChange({
            ...value,
            orgTypes,
        });
    };

    const handleAssignedUserChanged = (assignedUserId: SelectableUser) => {
        onChange({
            ...value,
            assignedUserId,
        });
    };

    const handleContactConditionChanged = useCallback(
        (condition: AdvancedSearchCondition | null) => {
            onChange({
                ...value,
                contactCondition: condition,
            });
        },
        [onChange, value],
    );

    const handleBusinessConditionChanged = useCallback(
        (condition: AdvancedSearchCondition | null) => {
            onChange({
                ...value,
                businessCondition: condition,
            });
        },
        [onChange, value],
    );

    const handleCommunityConditionChanged = useCallback(
        (condition: AdvancedSearchCondition | null) => {
            onChange({
                ...value,
                communityCondition: condition,
            });
        },
        [onChange, value],
    );

    const showClearButton = useMemo(() => {
        return (
            value.search.length > 0 ||
            value.orgTypes.length > 0 ||
            countFiltersApplied(value.contactCondition) > 0 ||
            countFiltersApplied(value.businessCondition) > 0 ||
            countFiltersApplied(value.communityCondition) > 0 ||
            value.assignedUserId !== EVERYONE_SENTINEL
        );
    }, [
        value.assignedUserId,
        value.search.length,
        value.orgTypes.length,
        value.contactCondition,
        value.businessCondition,
        value.communityCondition,
    ]);

    const handleGeoParamsChanged = (geoParams: SearchGeoParams | null) => {
        onChange({
            ...value,
            geoParams,
        });
    };

    const handleEntityConditionChanged = useCallback(
        (entityType: string, condition: AdvancedSearchCondition | null) => {
            if (entityType === "ReferenceContact") {
                handleContactConditionChanged(condition);
            } else if (entityType === "ReferenceBusiness") {
                handleBusinessConditionChanged(condition);
            } else if (entityType === "Community") {
                handleCommunityConditionChanged(condition);
            }
        },
        [
            handleBusinessConditionChanged,
            handleCommunityConditionChanged,
            handleContactConditionChanged,
        ],
    );

    const conditionConfigs: Record<string, EntityMoreFilterValue> = useMemo(() => {
        return {
            ReferenceContact: {
                condition: value.contactCondition,
                onChange: handleContactConditionChanged,
                label: "Contact filters",
                icon: "40_ReferralPerson",
                entityType: "ReferenceContact",
                layoutContainerKeys: [LayoutSectionParentKey.ReferenceContact],
            },
            ReferenceBusiness: {
                condition: value.businessCondition,
                onChange: handleBusinessConditionChanged,
                label: "Referral Organization filters",
                icon: "39_Business",
                entityType: "ReferenceBusiness",
                layoutContainerKeys: [LayoutSectionParentKey.ReferenceBusiness],
            },
            Community: {
                condition: value.communityCondition,
                onChange: handleCommunityConditionChanged,
                label: "Community filters",
                icon: "57_Community",
                entityType: "Community",
                layoutContainerKeys: [
                    LayoutSectionParentKey.CommunityDetailsColumn1,
                    LayoutSectionParentKey.CommunityDetailsColumn2,
                ],
            },
        };
    }, [
        value.contactCondition,
        value.businessCondition,
        value.communityCondition,
        handleBusinessConditionChanged,
        handleCommunityConditionChanged,
        handleContactConditionChanged,
    ]);

    return (
        <div className="max-2xl:space-y-4 2xl:flex 2xl:items-end 2xl:space-x-2">
            <div className="max-sm:space-y-4 sm:flex sm:items-end sm:space-x-2">
                <div className="2xl:w-80 max-2xl:flex-1">
                    <SearchInput
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder="Organization or contact name"
                    />
                </div>
                <div className="max-2xl:flex-1">
                    <UserSelect
                        label="Filter by assigned to"
                        onChange={handleAssignedUserChanged}
                        value={value.assignedUserId}
                        includeEveryone
                        restricted={!assignedToVisible}
                    />
                </div>
            </div>
            <div className="gap-2 max-lg:grid max-lg:grid-cols-2 lg:flex">
                <div className="lg:pb-2" ref={geoParamsRef}>
                    {onResetMapView ? (
                        <SearchGeoParamsCallout
                            value={value.geoParams}
                            onChange={handleGeoParamsChanged}
                            onResetMapView={onResetMapView}
                            align={geoParamsPosition.align}
                            arrowPosition={geoParamsPosition.arrowPosition}
                        />
                    ) : null}
                </div>
                <div className="lg:pb-2">
                    <OrgTypeFilter
                        value={value.orgTypes}
                        onChange={handleOrgTypeChanged}
                    />
                </div>
                <div className="lg:pb-2">
                    <EntityMoreFilter
                        conditions={[conditionConfigs.ReferenceContact]}
                        onConditionChange={handleEntityConditionChanged}
                        label="Contact filters"
                    />
                </div>
                <div className="lg:pb-2">
                    <EntityMoreFilter
                        conditions={[
                            conditionConfigs.ReferenceBusiness,
                            conditionConfigs.Community,
                        ]}
                        onConditionChange={handleEntityConditionChanged}
                        label="Organization filters"
                    />
                </div>
                {showClearButton ? (
                    <a
                        href="#"
                        className={`${defaultLinkStyle} text-sm lg:pb-2`}
                        onClick={e => {
                            e.preventDefault();
                            setSearchTerm("");
                            onChange({
                                search: "",
                                orgTypes: [],
                                contactCondition: null,
                                businessCondition: null,
                                communityCondition: null,
                                assignedUserId: EVERYONE_SENTINEL,
                                geoParams: null,
                            });
                        }}>
                        Clear all filters
                    </a>
                ) : null}
            </div>
        </div>
    );
};

interface OrgTypeFilterProps {
    value: string[];
    onChange: (orgTypes: string[]) => void;
}

const OrgTypeFilter: React.FC<OrgTypeFilterProps> = props => {
    const { value, onChange } = props;

    const [dynamicRef, dynamicPosition] = useDynamicCalloutPosition();
    const [itemFilterText, setItemFilterText] = useState("");

    const renderButton = useCallback(() => {
        return (
            <div className="flex items-center space-x-1 cursor-pointer text-gray-700 lg:whitespace-nowrap">
                <BuildingOfficeIcon className="w-5 h-5 flex-shrink-0 mr-2" />
                <span className="inline md:inline text-sm">Organization type</span>
                {value.length > 0 ? <SearchbarItemBadge counter={value.length} /> : null}
                <ChevronDownIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
            </div>
        );
    }, [value.length]);

    const addOrgType = (orgType: string) => {
        if (!value.includes(orgType)) {
            const newOrgTypes = [...value, orgType];
            onChange(newOrgTypes);
        }
    };

    const removeOrgType = (orgType: string) => {
        if (value.includes(orgType)) {
            const newOrgTypes = value.filter(ot => ot !== orgType);
            onChange(newOrgTypes);
        }
    };

    const clearOrgTypes = useCallback(() => {
        onChange([]);
    }, [onChange]);

    const headerGroups = useMemo(() => {
        const communityOption = {
            header: "Record Type",
            groupOptions: [
                {
                    text: "Community",
                    value: CommunityOrgTypeSentinel,
                },
            ],
        };

        return [...ReferralTypesOptions, communityOption]
            .map(o => {
                return {
                    header: o.header,
                    groupOptions: o.groupOptions.filter(
                        g =>
                            o.header
                                .toLowerCase()
                                .includes(itemFilterText.toLowerCase()) ||
                            g.text.toLowerCase().includes(itemFilterText.toLowerCase()),
                    ),
                };
            })
            .filter(o => o.groupOptions.length > 0);
    }, [itemFilterText]);

    return (
        <div ref={dynamicRef}>
            <Callout
                renderButton={renderButton}
                align={dynamicPosition.align}
                arrowPosition={dynamicPosition.arrowPosition}>
                <div className="space-y-2">
                    <SecondaryButton
                        onClick={e => {
                            e.preventDefault();
                            clearOrgTypes();
                        }}>
                        <span className="text-sm">Clear</span>
                    </SecondaryButton>
                    <SearchInput
                        value={itemFilterText}
                        onChange={e => {
                            setItemFilterText(e.target.value);
                        }}
                    />
                    <ul className="w-80">
                        {headerGroups.map((option, index) => (
                            <li key={index}>
                                <p className="font-bold">{option.header}</p>
                                <ul>
                                    {option.groupOptions.map(
                                        (groupOption, groupOptionIndex) => (
                                            <li key={groupOptionIndex}>
                                                <Checkbox
                                                    checked={value.includes(
                                                        groupOption.value,
                                                    )}
                                                    onChange={e => {
                                                        e.target.checked
                                                            ? addOrgType(
                                                                  groupOption.value,
                                                              )
                                                            : removeOrgType(
                                                                  groupOption.value,
                                                              );
                                                    }}
                                                    label={groupOption.text}
                                                />
                                            </li>
                                        ),
                                    )}
                                </ul>
                            </li>
                        ))}
                    </ul>
                </div>
            </Callout>
        </div>
    );
};
